import React, { useState} from "react";
import { Upload, message, Input, Card, Button, Row, Col, Collapse, Spin, Steps,Space, Tooltip } from "antd";
import { DownOutlined, UpOutlined, WarningOutlined } from "@ant-design/icons";
import { InboxOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { MedicalDisclaimer } from "../../components/MedicalDisclaimer";

// Declare global constants injected by Vite define
declare const API_BASE_URL: string;

const { TextArea } = Input;
const { Dragger } = Upload;
const { Panel } = Collapse;
const { Step } = Steps;

export const CodeIdentifier: React.FC = () => {
  type ICD10Code = {
    code: string;
    code_group?: string;
    short_description?: string;
    long_description?: string;
    reasoning?: string;
    is_fallback?: boolean;
    vector_search_score?: number;
  };

  type MedicalCondition = {
    condition_name: string;
    condition_description?: string;
    current_condition_status?: string;
    category?: string;
    best_icd10_codes?: ICD10Code[];
    possible_icd10_codes?: ICD10Code[];
    snippets?: string[];
    reasoning?: string;
    // legacy fields for compatibility, but not used in new UI
  };

  const { getAccessTokenSilently } = useAuth0();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [originalText, setOriginalText] = useState("");
  const [medicalConditions, setMedicalConditions] = useState<MedicalCondition[]>([]);
  const [finalEvaluation, setFinalEvaluation] = useState<MedicalCondition[]>([]);

  // Helper function to get all codes from a condition (handles both single and multiple codes)
  const getCodesFromCondition = (condition: MedicalCondition): ICD10Code[] => {
    if (condition.best_icd10_codes && condition.best_icd10_codes.length > 0) {
      return condition.best_icd10_codes;
    }
    // Fallback for backward compatibility
    if ((condition as any).best_icd10_code) {
      return [(condition as any).best_icd10_code];
    }
    return [];
  };
  const [patientNames, setPatientNames] = useState<string>("");
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [loadingIdentify, setLoadingIdentify] = useState(false);
  const [loadingMedicalConditions, setLoadingMedicalConditions] = useState(false);
  const [loadingFinalEvaluation, setLoadingFinalEvaluation] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const [activeKeys, setActiveKeys] = useState<(string | number)[]>([]);
  
    const allKeys = finalEvaluation.map((_, index) => index.toString());
  
    const isAllExpanded = activeKeys.length === allKeys.length;
  
    const toggleCollapseAll = () => {
      if (isAllExpanded) {
        setActiveKeys([]);
      } else {
        setActiveKeys(allKeys);
      }
    };
  
    const handleChange = (keys: React.Key | React.Key[]) => {
      const normalizedKeys = Array.isArray(keys)
        ? keys.map((key) => key.toString())
        : [keys.toString()];
      setActiveKeys(normalizedKeys);
    };



  const handleConditionChange = (index: number, field: string, value: string) => {
    setMedicalConditions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const getGroupedConditions = () => {
    const grouped = medicalConditions.reduce((acc, condition) => {
      const category = condition.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(condition);
      return acc;
    }, {} as Record<string, MedicalCondition[]>);

    // Sort categories alphabetically
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  };

  const getGroupedFinalEvaluation = () => {
    const grouped = finalEvaluation.reduce((acc, condition) => {
      const category = condition.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(condition);
      return acc;
    }, {} as Record<string, MedicalCondition[]>);

    // Sort categories alphabetically
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning("Please select at least one file.");
      return;
    }

    setLoadingUpload(true);

    try {
      const token = await getAccessTokenSilently();

      const formData = new FormData();
      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append("files", file.originFileObj);
        }
      });

      const response = await fetch(`${API_BASE_URL}/api/admin/extract-text/upload_and_process`, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Upload failed");

      message.success("Files uploaded and text extracted successfully!");
      setOriginalText(data.extracted_text?.original_text || "");
      setPatientNames(data.patient_names || "");
      setCurrentStep(1); // Move to the Extract Text step

    } catch (error) {
      message.error(`Upload failed: ${error}`);
    } finally {
      setLoadingUpload(false);
    }
  };

  const handleExtractText = async () => {
    setLoadingMedicalConditions(true);
    try {
      const token = await getAccessTokenSilently();
      
      // First, identify medical conditions using the current text
      const response = await fetch(`${API_BASE_URL}/api/admin/identify-medical-conditions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          original_text: originalText,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to identify conditions");

      setMedicalConditions(data.medical_conditions || []);
      message.success("Medical conditions identified successfully!");
      setCurrentStep(2); // Move to Identify Conditions step
    } catch (error) {
      message.error(`Failed to identify conditions: ${error}`);
    } finally {
      setLoadingMedicalConditions(false);
    }
  };

  const handleIdentifyCodes = async () => {
    if (!medicalConditions.length) {
      message.error("No medical conditions to process.");
      return;
    }

    setLoadingIdentify(true);
    setLoadingFinalEvaluation(true);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/admin/identify-codes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          original_text: originalText,
          medical_conditions: medicalConditions,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to identify codes.");

      message.success("Codes identified successfully!");
      // Use the new structure directly
      setFinalEvaluation(data.identified_codes || []);
      setCurrentStep(3); // Move to the final step
    } catch (error) {
      message.error(`Identification failed: ${error}`);
    } finally {
      setLoadingIdentify(false);
      setLoadingFinalEvaluation(false);
    }
  };

  const handleSubmitText = async () => {
    setLoadingUpload(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/admin/update-extracted-text`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          original_text: originalText,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed to update text");

      message.success("Text updated successfully!");
      setCurrentStep(2);
    } catch (error) {
      message.error(`Failed to update text: ${error}`);
    } finally {
      setLoadingUpload(false);
    }
  };

  return (
    <div>
      {/* Step-by-Step Progress Indicator */}
      <Row justify="center" style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={24} md={18} lg={16} xl={14}>
          <Steps current={currentStep} onChange={(current) => setCurrentStep(current)}>
            <Step title="Upload Files" />
            <Step title="Extract Text" />
            <Step title="Identify Conditions" />
            <Step title="Identify Codes" />
          </Steps>
        </Col>
      </Row>

      {/* File Upload Section */}
      {currentStep === 0 && (
        <Row justify="center" style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={24} md={18} lg={16} xl={14}>
            <Card title="Upload Medical Files" style={{ width: "100%" }}>
              <Dragger
                multiple
                fileList={fileList}
                beforeUpload={() => false}
                onChange={({ fileList }) => setFileList(fileList)}
                disabled={loadingUpload}
              >
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">Click or drag files here to upload</p>
                <p className="ant-upload-hint">Supports multiple file selection.</p>
              </Dragger>
              <Button
                type="primary"
                onClick={handleUpload}
                loading={loadingUpload}
                style={{ marginTop: "16px", width: "100%" }}
              >
                Upload and Extract Text
              </Button>
            </Card>
          </Col>
        </Row>
      )}

      {/* Extract Text Section */}
      {currentStep === 1 && (
        <Row gutter={[16, 16]} justify="center" style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={24} md={18} lg={16} xl={14}>
            <Card title="Extracted Text" style={{ width: "100%" }}>
              <div style={{ marginBottom: "16px" }}>
                <label><strong>Original Text:</strong></label>
                <TextArea
                  value={originalText}
                  onChange={(e) => setOriginalText(e.target.value)}
                  autoSize={{ minRows: 6, maxRows: 12 }}
                  style={{ marginTop: "8px" }}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "16px" }}>
                <Button onClick={() => setCurrentStep(0)}>Previous</Button>
                <Button type="primary" onClick={handleExtractText} loading={loadingMedicalConditions}>Next</Button>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Medical Conditions Section */}
      {currentStep === 2 && (
        <Row gutter={[16, 16]} justify="center" style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={24} md={18} lg={16} xl={14}>
            <Card
              title={
                <>
                  Medical Conditions found
                  {patientNames && (
                    <>
                      {" for "}
                      <span style={{ textDecoration: "underline" }}>{patientNames}</span>
                    </>
                  )}
                </>
              }
              style={{ width: "100%" }}
            >
              {loadingMedicalConditions ? (
                <div style={{ textAlign: "center", padding: "24px" }}>
                  <Spin />
                </div>
              ) : medicalConditions.length === 0 ? (
                <div style={{ textAlign: "center", padding: "24px" }}>
                  <p style={{ color: "#999" }}>No medical conditions found in the text.</p>
                </div>
              ) : (
                <>
                  {getGroupedConditions().map(([category, conditions]) => (
                    <div key={category} style={{ marginBottom: '24px' }}>
                      <h3 style={{ 
                        marginBottom: '16px', 
                        padding: '8px 16px',
                        background: '#f0f2f5',
                        borderRadius: '4px',
                        color: '#1f1f1f'
                      }}>
                        {category}
                      </h3>
                      <Collapse>
                        {conditions.map((condition, index) => {
                          const globalIndex = medicalConditions.indexOf(condition);
                          return (
                            <Panel header={condition.condition_name || `Condition ${globalIndex + 1}`} key={globalIndex}>
                              <div style={{ marginBottom: 8 }}>
                                <label><strong>Category:</strong></label>
                                <Input
                                  value={condition.category}
                                  onChange={(e) => handleConditionChange(globalIndex, "category", e.target.value)}
                                  placeholder="Enter Category"
                                />
                              </div>
                              <div style={{ marginBottom: 8 }}>
                                <label><strong>Condition Name:</strong></label>
                                <Input
                                  value={condition.condition_name}
                                  onChange={(e) => handleConditionChange(globalIndex, "condition_name", e.target.value)}
                                  placeholder="Enter Condition Name"
                                />
                              </div>
                              <div style={{ marginBottom: 8 }}>
                                <label><strong>Condition Description:</strong></label>
                                <TextArea
                                  value={condition.condition_description}
                                  onChange={(e) => handleConditionChange(globalIndex, "condition_description", e.target.value)}
                                  placeholder="Enter Condition Description"
                                  autoSize
                                />
                              </div>
                              <div style={{ marginBottom: 8 }}>
                                <label><strong>Current Condition Status:</strong></label>
                                <Input
                                  value={condition.current_condition_status}
                                  onChange={(e) => handleConditionChange(globalIndex, "current_condition_status", e.target.value)}
                                  placeholder="Enter Current Status"
                                />
                              </div>
                              <div>
                                <label><strong>Relevant Snippets:</strong></label>
                                <div style={{ marginTop: 8, maxHeight: '300px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '4px', padding: '12px' }}>
                                  {condition.snippets && condition.snippets.length > 0 ? (
                                    <ul style={{ 
                                      listStyle: 'none', 
                                      padding: 0, 
                                      margin: 0,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '12px'
                                    }}>
                                      {condition.snippets.map((snippet, snippetIndex) => (
                                        <li key={snippetIndex} style={{
                                          padding: '12px',
                                          background: '#fafafa',
                                          borderRadius: '4px',
                                          border: '1px solid #f0f0f0'
                                        }}>
                                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                            <span style={{ 
                                              background: '#1890ff', 
                                              color: 'white', 
                                              padding: '2px 8px', 
                                              borderRadius: '12px',
                                              fontSize: '12px',
                                              minWidth: '24px',
                                              textAlign: 'center'
                                            }}>
                                              {snippetIndex + 1}
                                            </span>
                                            <span style={{ flex: 1 }}>{snippet}</span>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <div style={{ color: '#999', textAlign: 'center', padding: '24px' }}>
                                      No snippets available
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Panel>
                          );
                        })}
                      </Collapse>
                    </div>
                  ))}
                  <Button
                    type="primary"
                    onClick={handleIdentifyCodes}
                    loading={loadingIdentify}
                    style={{ marginTop: "16px", width: "100%" }}
                  >
                    Identify Codes
                  </Button>
                </>
              )}
            </Card>
            {/* Navigation */}
            <div style={{ marginTop: "16px", display: "flex", justifyContent: "space-between" }}>
              <Button onClick={() => setCurrentStep(1)}>Previous</Button>
            </div>
          </Col>
        </Row>
      )}

      {/* Final Evaluation Section */}
      {currentStep === 3 && (
        <Row gutter={[16, 16]} justify="center" style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={24} md={18} lg={16} xl={14}>
            <Card
              title={
                <>
                  Analyzed Conditions with Best Suited Code
                  {patientNames && (
                    <>
                      {" for "}
                      <span style={{ textDecoration: "underline" }}>{patientNames}</span>
                    </>
                  )}
                </>
              }
              style={{ width: "100%" }}
            >
              {loadingFinalEvaluation ? (
                <div style={{ textAlign: "center", padding: "24px" }}>
                  <Spin />
                </div>
              ) : finalEvaluation.length === 0 ? (
                <p style={{ textAlign: "center", color: "#999" }}>No data available</p>
              ) : (
                <>
                  {getGroupedFinalEvaluation().map(([category, conditions]) => (
                    <div key={category} style={{ marginBottom: '24px' }}>
                      <h3 style={{ 
                        marginBottom: '16px', 
                        padding: '8px 16px',
                        background: '#f0f2f5',
                        borderRadius: '4px',
                        color: '#1f1f1f'
                      }}>
                        {category}
                      </h3>
                      <Collapse activeKey={activeKeys} onChange={handleChange}>
                        {conditions.map((item, index) => (
                          <Panel
                            header={
                              <>
                                {item.condition_name} (
                                <strong>Code: {(() => {
                                  const codes = getCodesFromCondition(item);
                                  if (codes.length === 0) return "N/A";
                                  if (codes.length === 1) return codes[0].code;
                                  return `${codes.length} codes`;
                                })()}</strong>
                                )

                              </>
                            }
                            key={`${category}-${index}`}
                          >
                            <p>
                              <b>Condition Description:</b> <br />
                              {item.condition_description}
                            </p>
                            {item.reasoning && (
                              <p>
                                <b>Reasoning:</b> <br />
                                {item.reasoning}
                              </p>
                            )}
                            {item.snippets && item.snippets.length > 0 && (
                              <Card type="inner" title="Provider Notes" style={{ marginBottom: 16 }}>
                                {item.snippets.map((snippet, i) => (
                                  <p key={i} style={{ 
                                    backgroundColor: '#f5f5f5',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    marginBottom: '8px',
                                    fontStyle: 'italic'
                                  }}>
                                    "{snippet}"
                                  </p>
                                ))}
                              </Card>
                            )}
                            {(() => {
                              const codes = getCodesFromCondition(item);
                              if (codes.length === 0) return null;
                              
                              return codes.map((code, codeIndex) => (
                                <Card 
                                  key={`code-${codeIndex}`}
                                  type="inner" 
                                  title={`${codes.length > 1 ? `Code ${codeIndex + 1}: ` : 'Best Match Code: '}${code.code}`}
                                  style={{ marginBottom: codeIndex < codes.length - 1 ? 16 : 0 }}
                                > 
                                  <p>
                                    <strong>Description:</strong> {code.short_description}
                                  </p>
                                  {code.long_description && (
                                    <p>
                                      <strong>Long Description:</strong> {code.long_description}
                                    </p>
                                  )}
                                  {code.reasoning && (
                                    <p>
                                      <strong>Reasoning:</strong> {code.reasoning}
                                    </p>
                                  )}
                                </Card>
                              ));
                            })()}
                            {item.possible_icd10_codes && item.possible_icd10_codes.length > 0 && (
                              <Card type="inner" title="All Possible Codes" style={{ marginTop: 16 }}>
                                <ul style={{ paddingLeft: 20 }}>
                                  {item.possible_icd10_codes.map((code, j) => (
                                    <li key={j}>
                                      <strong>{code.code}:</strong> {code.short_description}
                                    </li>
                                  ))}
                                </ul>
                              </Card>
                            )}
                          </Panel>
                        ))}
                      </Collapse>
                    </div>
                  ))}
                </>
              )}
            </Card>
            <div style={{ marginTop: "16px", display: "flex", justifyContent: "space-between" }}>
              <Button onClick={() => setCurrentStep(2)}>Previous</Button>
            </div>
          </Col>
        </Row>
      )}

      {/* Medical Disclaimer */}
      <Row justify="center">
        <Col xs={24} sm={24} md={18} lg={16} xl={14}>
          <MedicalDisclaimer />
        </Col>
      </Row>
    </div>
  );
};